import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye } from "lucide-react";
import api from "../lib/api";
import { useLocale } from "../contexts/LocaleContext";
import QuickViewModal from "../components/QuickViewModal";

export default function Shop() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [filters, setFilters] = useState({ brand: params.get("brand") || "", category: params.get("category") || "", q: params.get("q") || "", gender: params.get("gender") || "" });
  const [loading, setLoading] = useState(false);
  const [qvId, setQvId] = useState(null);
  const { fmt } = useLocale();

  const fetch = async () => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => v && q.set(k, v));
    const r = await api.get(`/products?${q.toString()}`);
    setItems(r.data.items || []);
    setLoading(false);
  };

  useEffect(() => { api.get("/brands").then(r => setBrands(r.data)); api.get("/categories").then(r => setCats(r.data)); }, []);
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [filters]);

  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-14">
      <div className="mb-10">
        <div className="overline mb-3">The Collection</div>
        <h1 className="font-display text-5xl md:text-7xl text-dark tracking-tight">All Watches</h1>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-10">
        <aside className="space-y-8">
          <div>
            <div className="overline mb-3">Search</div>
            <input data-testid="shop-search" placeholder="Search…" className="input-luxe" value={filters.q} onChange={(e)=>setFilters({...filters, q: e.target.value})}/>
          </div>
          <div>
            <div className="overline mb-3">Brand</div>
            <div className="space-y-1.5">
              <button onClick={()=>setFilters({...filters, brand:""})} className={`block text-sm ${!filters.brand?"text-copper":"text-gray-600 hover:text-dark"}`}>All Brands</button>
              {brands.map(b => (
                <button key={b.id} data-testid={`filter-brand-${b.id}`} onClick={()=>setFilters({...filters, brand: b.id})} className={`block text-sm ${filters.brand===b.id?"text-copper":"text-gray-600 hover:text-dark"}`}>{b.name}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-3">Category</div>
            <div className="space-y-1.5">
              <button onClick={()=>setFilters({...filters, category:""})} className={`block text-sm ${!filters.category?"text-copper":"text-gray-600 hover:text-dark"}`}>All</button>
              {cats.map(c => (
                <button key={c.id} onClick={()=>setFilters({...filters, category: c.id})} className={`block text-sm ${filters.category===c.id?"text-copper":"text-gray-600 hover:text-dark"}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-3">Gender</div>
            <div className="space-y-1.5">
              {["", "men", "women", "unisex"].map(g => (
                <button key={g||"any"} onClick={()=>setFilters({...filters, gender: g})} className={`block text-sm ${filters.gender===g?"text-copper":"text-gray-600 hover:text-dark"}`}>{g||"Any"}</button>
              ))}
            </div>
          </div>
        </aside>

        <div>
          <div className="text-xs text-gray-500 uppercase tracking-luxe mb-6">{loading? "Loading…" : `${items.length} pieces`}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12">
            {items.map(p => (
              <div key={p.id} className="group relative">
                <Link to={`/product/${p.id}`} data-testid={`product-${p.id}`} className="product-card block">
                  <div className="aspect-square overflow-hidden bg-[#FAF7F2] relative">
                    <img src={p.images?.[0]} alt={p.name} loading="lazy" onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=900"; }} className="w-full h-full object-cover"/>
                    {p.is_new && <span className="absolute top-3 left-3 bg-copper text-white text-[9px] font-semibold uppercase tracking-luxe px-2 py-1">New</span>}
                    {p.original_price && p.original_price > p.price && <span className="absolute top-3 right-3 bg-red-600 text-white text-[10px] font-semibold px-2 py-1">−{Math.round((1 - p.price/p.original_price)*100)}%</span>}
                    <button
                      type="button"
                      data-testid={`shop-qv-${p.id}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQvId(p.id); }}
                      className="absolute bottom-3 left-3 right-3 bg-white text-dark text-[10px] uppercase tracking-luxe py-2 flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-dark hover:text-white"
                    >
                      <Eye size={12}/> Quick View
                    </button>
                  </div>
                  <div className="pt-4">
                    <div className="text-[11px] uppercase tracking-luxe text-copper">{p.brand}</div>
                    <div className="font-display text-lg text-dark mt-1">{p.name}</div>
                    <div className="text-sm mt-2">{p.original_price && p.original_price > p.price ? <><span className="text-dark font-semibold">{fmt(p.price)}</span><span className="text-gray-400 line-through ml-2 text-xs">{fmt(p.original_price)}</span></> : <span className="text-gray-700">{fmt(p.price)}</span>}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          {items.length === 0 && !loading && <div className="text-center py-20 text-gray-400">No watches match your filters.</div>}
        </div>
      </div>
      <QuickViewModal productId={qvId} onClose={() => setQvId(null)}/>
    </div>
  );
}
